import React from 'react';
import { Phone } from 'lucide-react';
import { PageLayout } from '../components/global/PageLayout';
import { PageIntro, Section } from '../components/ui/PageIntro';
import { EventCard } from '../components/LowOnFuel/EventCard';
import { CTABlock } from '../components/ui/CTABlock';
import { getPublishedEvents } from '../data/events';
import { IMAGES } from '../data/images';
import { CONTACT, PAGE_META } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function WhatsOn() {
  useDocumentMeta({
    ...PAGE_META.whatsOn,
    path: '/whats-on'
  });
  const published = getPublishedEvents();
  const walkIn = published.filter(event => event.booking === 'walk-in');
  const bookable = published.filter(event => event.booking === 'bookable');
  return <PageLayout data-id="element-879" data-mp="2jt7s7atas8ji">
      <PageIntro eyebrow="What's on" title="What's coming down the road" intro="Friday Bike Night is the one to know about, and it runs every week without fail. There is nothing to book and no cover charge — park up, get a drink and find a spot. Anything else we put on, this is where it will appear." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: "What's on"
    }]} image={IMAGES.bikeNightSign.src} data-id="element-880" data-mp="2jtqz7whetcir" />

      {/* Walk-in first. Bike Night is the reason most people are on this
          page, and putting a booking form ahead of it would imply the
          weekly ritual is the thing that needs arranging. */}
      {walkIn.length > 0 && <Section labelledBy="walkin-heading" data-id="element-881" data-mp="1uetitciwm4cv">
          <h2 id="walkin-heading" className="font-bebas text-4xl leading-none tracking-wide text-ink sm:text-5xl" data-id="element-882" data-mp="1udqpyz20trub">
            Just turn up
          </h2>
          <p className="mt-3 max-w-prose leading-relaxed text-ink/75" data-id="element-883" data-mp="1kfkrysjc55uq">
            No booking, no cover charge and no list. Park up, get yourself a
            drink and find a spot inside, on the terrace or out on the deck.
          </p>

          <ul className={`mt-8 grid gap-6 ${walkIn.length === 1 ? 'max-w-md' : 'sm:grid-cols-2'}`} data-id="element-884" data-mp="8zdzsf1fyi9v">
            {walkIn.map(event => <li key={event.id} className="flex" data-id="element-885" data-mp="26re1renlwxxj">
                <div className="flex-1" data-id="element-886" data-mp="21xszvbiw7jt2">
                  <EventCard event={event} data-id="element-887" data-mp="21wrm2zahsj7n" />
                </div>
              </li>)}
          </ul>
        </Section>}

      {/* Only rendered when something actually needs booking. With the
          guided run and wrench night hidden (D41) there is nothing, and a
          "Worth booking ahead" heading above an empty grid would read as a
          broken page rather than an honest one. */}
      {bookable.length > 0 && <div className="border-y border-hairline bg-cream" data-id="element-888" data-mp="1e15ej8e7rnc3">
          <Section labelledBy="bookable-heading" className="py-12 sm:py-16" data-id="element-889" data-mp="1e2q8owwm949f">
            <h2 id="bookable-heading" className="font-bebas text-4xl leading-none tracking-wide text-ink sm:text-5xl" data-id="element-890" data-mp="3dd7zkn2hkprv">
              Worth booking ahead
            </h2>
            <p className="mt-3 max-w-prose leading-relaxed text-ink/75" data-id="element-891" data-mp="3gtkun1shjeis">
              There are only so many places on these, so booking is the one
              way to be sure of yours.
            </p>

            <ul className={`mt-8 grid gap-6 ${bookable.length === 1 ? 'max-w-md' : 'sm:grid-cols-2'}`} data-id="element-892" data-mp="3pq25e3wxtae0">
              {bookable.map(event => <li key={event.id} className="flex" data-id="element-893" data-mp="2xz5w1da35nvh">
                  <div className="flex-1" data-id="element-894" data-mp="31zg20pxvoeac">
                    <EventCard event={event} data-id="element-895" data-mp="3207uhrw6hvwe" />
                  </div>
                </li>)}
            </ul>
          </Section>
        </div>}

      {/* Stands on its own rather than inside the bookable band, so it
          survives that band disappearing. Groups are the one case where a
          walk-in place still wants a word in advance. */}
      <div className="border-y border-hairline bg-cream" data-id="element-896" data-mp="r4s96zw5leg0">
        <Section labelledBy="groups-heading" className="py-12 sm:py-16" data-id="element-897" data-mp="202s2sodid6og">
          <div className="flex flex-col gap-4 rounded-container border border-hairline bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-898" data-mp="2054sjnpiclc2">
            <div data-id="element-899" data-mp="2951gfn42pg62">
              {/* h2, not h3: with the bookable band gone this is a top-level
                  section of the page, and a lone h3 would skip a level. */}
              <h2 id="groups-heading" className="font-bebas text-2xl tracking-wide text-ink" data-id="element-900" data-mp="2j1mhxygvw955">
                Group and club bookings
              </h2>
              <p className="mt-1 max-w-prose text-sm leading-relaxed text-ink/80" data-id="element-901" data-mp="2ruzinml6wf1s">
                Give us a ring and we'll keep you room on the deck and space
                to park up.
              </p>
            </div>
            <a href={CONTACT.telephoneHref} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-container bg-moss px-5 py-3 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-moss-dark focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-902" data-mp="31tp0xgh001ik">
              <Phone className="h-4 w-4" aria-hidden="true" data-id="element-903" data-mp="36hoixqn9mw98" />
              {CONTACT.telephone}
            </a>
          </div>
        </Section>
      </div>

      <CTABlock data-id="element-904" data-mp="3bickh694evhp" />
    </PageLayout>;
}